// RTT-over-time chart (owned by the web:charts agent).
//
// uPlot line of round-trip time vs elapsed seconds, with a dashed
// acceptable-delay threshold line and lost-packet ticks along the top of the
// plot (red = upstream loss, purple = downstream loss). Drag-select on the plot
// zooms the x-axis; double-click resets (uPlot defaults). The chart restyles
// live on theme change because its options are rebuilt from the palette.

import { useMemo } from 'react';
import uPlot from 'uplot';
import type { RunResult } from '@/engine/types';
import { UplotChart } from './Uplot';
import { EmptyNote } from './Canvas';
import { useChartTheme, withAlpha, type ChartPalette } from './palette';

export interface RttOverTimeProps {
  run: RunResult;
  height?: number;
}

interface Prepared {
  data: uPlot.AlignedData;
  lostUpX: number[];
  lostDownX: number[];
  hasData: boolean;
}

function prepare(run: RunResult): Prepared {
  const startUs = run.startedAtUs;
  const xs: number[] = [];
  const rtt: (number | null)[] = [];
  const lostUpX: number[] = [];
  const lostDownX: number[] = [];
  for (const p of run.packets) {
    if (p.cls === 'notSent') continue;
    const t = (p.sentUs - startUs) / 1e6;
    xs.push(t);
    rtt.push(p.rttMs);
    if (p.cls === 'lostUp' || p.cls === 'unknown') lostUpX.push(t);
    else if (p.cls === 'lostDown') lostDownX.push(t);
  }
  const thr = xs.map(() => run.settings.acceptableDelayMs);
  return { data: [xs, rtt, thr] as uPlot.AlignedData, lostUpX, lostDownX, hasData: xs.length > 0 };
}

function lostTicksPlugin(
  palette: ChartPalette,
  lostUpX: number[],
  lostDownX: number[],
): uPlot.Plugin {
  const tickLen = 8;
  const draw = (u: uPlot) => {
    const ctx = u.ctx;
    const top = u.bbox.top;
    const left = u.bbox.left;
    const right = left + u.bbox.width;
    ctx.save();
    ctx.lineWidth = Math.max(1, Math.round((window.devicePixelRatio || 1)));
    const drawSet = (xsArr: number[], color: string) => {
      ctx.strokeStyle = color;
      ctx.beginPath();
      for (const x of xsArr) {
        const px = u.valToPos(x, 'x', true);
        if (px < left || px > right) continue;
        ctx.moveTo(px, top);
        ctx.lineTo(px, top + tickLen * (window.devicePixelRatio || 1));
      }
      ctx.stroke();
    };
    drawSet(lostDownX, palette.lostDown);
    drawSet(lostUpX, palette.lostUp);
    ctx.restore();
  };
  return { hooks: { draw } };
}

export function RttOverTime({ run, height = 260 }: RttOverTimeProps) {
  const { palette } = useChartTheme();
  const prep = useMemo(() => prepare(run), [run]);

  const options = useMemo<Omit<uPlot.Options, 'width'>>(() => {
    return {
      height,
      cursor: { drag: { x: true, y: false }, sync: { key: `run-${run.nodeId}` } },
      legend: { show: true },
      scales: { x: { time: false }, y: { auto: true } },
      axes: [
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          ticks: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'Elapsed (s)',
          labelFont: '12px system-ui, sans-serif',
        },
        {
          stroke: palette.axis,
          grid: { stroke: palette.grid },
          ticks: { stroke: palette.grid },
          font: '11px system-ui, sans-serif',
          label: 'RTT (ms)',
          labelFont: '12px system-ui, sans-serif',
        },
      ],
      series: [
        { label: 't' },
        {
          label: 'RTT',
          stroke: palette.accent,
          width: 1.5,
          fill: withAlpha(palette.accent, 0.08),
          points: { show: prep.data[0].length <= 400, size: 4 },
          spanGaps: false,
        },
        {
          label: 'Acceptable',
          stroke: palette.threshold,
          width: 1,
          dash: [5, 4],
          points: { show: false },
        },
      ],
      plugins: [lostTicksPlugin(palette, prep.lostUpX, prep.lostDownX)],
    };
  }, [palette, height, run.nodeId, prep]);

  if (!prep.hasData) {
    return <EmptyNote label="No packets to plot yet." />;
  }

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <UplotChart options={options} data={prep.data} ariaLabel="Round-trip time over time" />
      </div>
      <figcaption style={{ fontSize: 12, color: palette.textMuted, marginTop: 4 }}>
        Ticks at top mark lost packets ({colorSwatch(palette.lostUp)} upstream,{' '}
        {colorSwatch(palette.lostDown)} downstream). Drag to zoom, double-click to reset.
      </figcaption>
    </figure>
  );
}

function colorSwatch(color: string) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: 2,
        background: color,
        margin: '0 2px',
      }}
    />
  );
}
